'use client';

import { Stack, Typography } from '@mui/material';
import type { InventoryResource } from '@repo/api-gw-sdk';
import { CdcBackupModeCheckResultModeEnum, UpdateCloudResourceConfigurationRequestSetDbBackupModeEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { OptionMenu } from '@/components/option-menu/option-menu';
import type { OptionMenuItemType } from '@/components/option-menu/option-menu';
import { Loader } from '@/components/shared/loader';
import { useDAL } from '@/contexts/useDAL';
import { useIsDevModeEnabled } from '@/contexts/useIsDevModeEnabled';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import { RdsCdcOnboardingDialog } from './rdsCdcOnboardingDialog';
import { deriveCdcSteps, useRdsCdcStatus } from './useRdsCdcStatus';
const DEFAULT_LABEL = 'Default';
const CDC_LABEL = 'CDC';
const CDC_READER_ONLY_LABEL = 'CDC Reader only';
export const RdsCdcModeField = ({
  item
}: {
  item: InventoryResource;
}) => {
  const dal = useDAL();
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const [isUpdating, setIsUpdating] = useState(false);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const isDevMode = useIsDevModeEnabled();

  // Two sources on purpose. The label comes from the configuration key read, which is a database
  // lookup; check-cdc-prerequisites scans RAM shares and parameter groups in AWS and measured 2-9s
  // locally, so gating the row's text on it left "Loading..." on screen for seconds.
  const {
    data: configuration,
    isLoading: isConfigurationLoading,
    error: configurationError,
    mutate: refreshConfiguration
  } = dal.inventory.configuration.useGet(item.id);
  const {
    isLoading: isCheckLoading,
    refresh: refreshChecks,
    isEligible,
    ineligibleReason,
    isPrivateLinkProvisioning,
    mode,
    allStepsDone
  } = useRdsCdcStatus(item.id);
  const refresh = async () => {
    await Promise.all([refreshConfiguration(), refreshChecks()]);
  };
  const setMode = async (setDbBackupMode: UpdateCloudResourceConfigurationRequestSetDbBackupModeEnum) => {
    setIsUpdating(true);
    try {
      await dal.inventory.configuration.update(item.id, {
        setDbBackupMode
      });
      await refresh();
    } catch (updateError) {
      snackbar.showErrorMessage(await extractErrorMessage(updateError));
    } finally {
      setIsUpdating(false);
    }
  };

  // The config write is not validated server-side, so this is the only gate: re-check first and
  // commit only once all three onboarding steps pass, otherwise send the user to them. Dev mode
  // deliberately skips the gate so employees can force the mode on a half-configured resource.
  const selectCdc = async () => {
    if (isDevMode) {
      await setMode(UpdateCloudResourceConfigurationRequestSetDbBackupModeEnum.Cdc);
      return;
    }
    setIsUpdating(true);
    try {
      const latest = await refreshChecks();
      if (!latest?.eligible || !deriveCdcSteps(latest).allStepsDone) {
        setIsOnboardingOpen(true);
        return;
      }
      await dal.inventory.configuration.update(item.id, {
        setDbBackupMode: UpdateCloudResourceConfigurationRequestSetDbBackupModeEnum.Cdc
      });
      await refresh();
      snackbar.showMessage('CDC enabled for this database');
    } catch (updateError_0) {
      snackbar.showErrorMessage(await extractErrorMessage(updateError_0));
    } finally {
      setIsUpdating(false);
    }
  };
  if (isConfigurationLoading) {
    return <Loader size='medium' />;
  }
  if (configurationError) {
    return <Typography>N/A</Typography>;
  }
  const canUpdate = isAuthorized('update:inventory');
  // dbBackupModeCdc is a boolean, so it cannot express CDC_READER_ONLY. Dev mode is the only place
  // that mode is selectable, and it is the only place that pays the slow check's latency for a label.
  const isCdcReaderOnly = isDevMode && mode === CdcBackupModeCheckResultModeEnum.CdcReaderOnly;
  const isCdcSelected = !!configuration?.dbBackupModeCdc;
  const displayText = isCdcReaderOnly ? CDC_READER_ONLY_LABEL : isCdcSelected ? CDC_LABEL : DEFAULT_LABEL;

  // Eligibility rides the slow check, so until it lands the option stays disabled with a distinct
  // reason rather than silently looking unavailable.
  const unavailableReason = isCheckLoading ? 'Checking prerequisites...' : !isEligible ? ineligibleReason ?? 'CDC is not available for this database' : undefined;
  // The hub renders its own loader and its own ineligible message, so the entry to it stays open while
  // the slow check is in flight: gating it would hide the setup behind seconds of a dead menu item.
  const setupDisabledReason = !canUpdate ? 'No permissions to perform this action' : isCheckLoading ? undefined : unavailableReason;
  const cdcDisabledTooltip = unavailableReason ?? (isPrivateLinkProvisioning ? 'AWS PrivateLink is still provisioning' : !allStepsDone ? 'Complete the setup via Configure CDC first' : undefined);
  const options: OptionMenuItemType[] = [{
    id: 'default',
    text: DEFAULT_LABEL,
    menuItemProps: {
      selected: !isCdcSelected && !isCdcReaderOnly,
      disabled: !canUpdate || !isCdcSelected && !isCdcReaderOnly,
      onClick: () => void setMode(UpdateCloudResourceConfigurationRequestSetDbBackupModeEnum.None)
    }
  }, {
    id: 'cdc',
    text: CDC_LABEL,
    // Before the setup is verified, switching to CDC would only reopen the same hub that
    // Configure CDC opens, so the option waits for the setup instead. Dev mode keeps its bypass
    // for forcing the mode on a half-configured resource.
    isDisabled: () => ({
      disabled: !canUpdate || isCdcSelected || !isDevMode && (isCheckLoading || !isEligible || isPrivateLinkProvisioning || !allStepsDone),
      tooltip: cdcDisabledTooltip
    }),
    menuItemProps: {
      selected: isCdcSelected,
      onClick: () => void selectCdc()
    }
  }, ...(isDevMode ? [{
    id: 'cdc-reader-only',
    text: CDC_READER_ONLY_LABEL,
    menuItemProps: {
      selected: isCdcReaderOnly,
      disabled: !canUpdate || isCdcReaderOnly,
      onClick: () => void setMode(UpdateCloudResourceConfigurationRequestSetDbBackupModeEnum.CdcReaderOnly)
    }
  }] : []), {
    id: 'setup-divider',
    divider: true
  }, {
    id: 'review-setup',
    text: isCdcSelected || allStepsDone ? 'Review CDC setup' : 'Configure CDC',
    // The hub's three steps are Postgres-specific, so there is nothing to review on a resource the
    // server reports as ineligible. The hub's actions are all writes, so it also stays closed to
    // viewers who cannot update the inventory.
    isDisabled: () => ({
      disabled: !!setupDisabledReason,
      tooltip: setupDisabledReason
    }),
    menuItemProps: {
      onClick: () => setIsOnboardingOpen(true)
    }
  }];
  return <Stack direction='row' sx={{
    gap: '8px',
    alignItems: 'center'
  }}>
      <Typography>{displayText}</Typography>
      {isUpdating && <Loader size='medium' />}
      {!isUpdating && <OptionMenu options={options} />}
      {isOnboardingOpen && <RdsCdcOnboardingDialog resourceId={item.id} region={item.region} isCdcEnabled={isCdcSelected} onClose={() => setIsOnboardingOpen(false)} onEnabled={() => {
      setIsOnboardingOpen(false);
      void refresh();
    }} />}
    </Stack>;
};